<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import type { Trip, Expense, ExpenseCategory, Companion, ExpenseParticipant } from "../lib/types";
  import { expensesApi } from "../lib/api";
  import { addNotification } from "../lib/stores";
  import ExpenseList from "../components/data-display/ExpenseList.svelte";
  import ExpenseDetailModal from "../components/data-display/ExpenseDetailModal.svelte";
  import ExpenseParticipantSelector from "../components/data-display/ExpenseParticipantSelector.svelte";
  import Modal from "../components/overlay/Modal.svelte";
  import EmptyState from "../components/feedback/EmptyState.svelte";
  import Layout from "../components/layout/Layout.svelte";
  import TripSubNav from "../components/navigation/TripSubNav.svelte";
  import { categoryIcons, entryIcons } from "../lib/icons";
  import Button from "../components/input/Button.svelte";
  import { formatCurrency } from "../lib/utils/format";

  let { trip, expenses = [], companions = [], meta = null, user = null }: {
    trip: Trip;
    expenses?: Expense[];
    companions?: Companion[];
    meta?: { page: number; total: number; totalPages: number } | null;
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  import type { EntryMethod } from "../lib/types";

  let showCreateModal = $state(false);
  let showDetailModal = $state(false);
  let showParticipantSelector = $state(false);
  let selectedExpense: Expense | null = $state(null);
  let selectedExpenseId: string | null = $state(null);
  let selectedExpenseParticipants: ExpenseParticipant[] = $state([]);
  let loading = $state(false);
  let analyzing = $state(false);
  let fileInput: HTMLInputElement;
  let selectedCategory: ExpenseCategory | "" = $state("");
  let selectedEntryMethod: EntryMethod | "" = $state("");

  function openExpenseDetail(expense: Expense) {
    selectedExpense = expense;
    showDetailModal = true;
  }

  function closeExpenseDetail() {
    showDetailModal = false;
    selectedExpense = null;
  }

  function openParticipantSelector(expense: Expense) {
    selectedExpenseId = expense.id;
    selectedExpenseParticipants = expense.participants || [];
    showParticipantSelector = true;
  }

  function closeParticipantSelector() {
    showParticipantSelector = false;
    selectedExpenseId = null;
    selectedExpenseParticipants = [];
  }

  function handleParticipantsSaved() {
    router.reload();
  }

  const categories: Array<{ key: ExpenseCategory; label: string; icon: string }> = [
    { key: "food", label: "식비", icon: categoryIcons.food },
    { key: "transport", label: "교통", icon: categoryIcons.transport },
    { key: "accommodation", label: "숙박", icon: categoryIcons.accommodation },
    { key: "shopping", label: "쇼핑", icon: categoryIcons.shopping },
    { key: "activity", label: "액티비티", icon: categoryIcons.activity },
    { key: "other", label: "기타", icon: categoryIcons.other },
  ];

  let newExpense = $state({
    merchantName: "",
    originalAmountCents: 0,
    originalCurrency: "KRW",
    category: "food" as ExpenseCategory,
    description: "",
    transactedAt: new Date().toISOString().split("T")[0],
  });

  let filteredExpenses = $derived(expenses
    .filter((e) => !selectedCategory || e.category === selectedCategory)
    .filter((e) => !selectedEntryMethod || e.entryMethod === selectedEntryMethod));

  // 입력 방식별 개수
  let entryMethodCounts = $derived({
    manual: expenses.filter((e) => e.entryMethod === "manual" || !e.entryMethod).length,
    ocr: expenses.filter((e) => e.entryMethod === "ocr").length,
    withReceipt: expenses.filter((e) => e.hasReceipt).length,
  });

  async function createExpense() {
    loading = true;
    try {
      const response = await expensesApi.create(trip.id, {
        merchantName: newExpense.merchantName,
        originalAmountCents: newExpense.originalAmountCents * 100,
        originalCurrency: newExpense.originalCurrency,
        category: newExpense.category,
        description: newExpense.description,
        transactedAt: newExpense.transactedAt,
      });

      if (response.success) {
        addNotification("success", "지출이 추가되었습니다!");
        showCreateModal = false;
        resetForm();
        router.reload();
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        addNotification("error", error.response.data.errors[0]?.message || "지출 추가에 실패했습니다.");
      } else {
        addNotification("error", "지출 추가에 실패했습니다.");
      }
    } finally {
      loading = false;
    }
  }

  async function handleFileUpload(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    analyzing = true;
    try {
      const result = await expensesApi.ocr(file);
      if (result.success && result.data) {
        const { merchant_name, amount, currency, date, category } = result.data;
        
        // Update form with OCR results
        newExpense = {
          ...newExpense,
          merchantName: merchant_name || newExpense.merchantName,
          originalAmountCents: amount || newExpense.originalAmountCents,
          originalCurrency: currency || newExpense.originalCurrency,
          category: (category as ExpenseCategory) || newExpense.category,
          transactedAt: date || newExpense.transactedAt,
        };
        
        addNotification("success", "영수증이 인식되었습니다!");
      } else {
        addNotification("error", "영수증 정보를 읽을 수 없습니다.");
      }
    } catch (error: any) {
      console.error("OCR Error:", error);
      addNotification("error", error.response?.data?.error?.message || "영수증 인식에 실패했습니다.");
    } finally {
      analyzing = false;
      // Reset input so same file can be selected again
      if (fileInput) fileInput.value = "";
    }
  }

  function resetForm() {
    newExpense = {
      merchantName: "",
      originalAmountCents: 0,
      originalCurrency: "KRW",
      category: "food",
      description: "",
      transactedAt: new Date().toISOString().split("T")[0],
    };
  }

  // Calculate category totals
  let categoryTotals = $derived(expenses.reduce((acc, expense) => {
    acc[expense.category] = (acc[expense.category] || 0) + expense.krwAmountCents;
    return acc;
  }, {} as Record<string, number>));
</script>

<Layout {user}>
  <div class="min-h-screen bg-app">
    <!-- Trip Sub Navigation -->
    <TripSubNav {trip} currentPage="expenses" />

    <!-- Header -->
    <header class="bg-surface border-b border">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-lg font-bold text-primary flex items-center gap-2">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
              지출 내역
            </h2>
            <p class="text-secondary text-sm">
              총 {meta?.total || expenses.length}건 · ₩{formatCurrency(trip.spentCents)}
            </p>
          </div>
          <Button variant="primary" onclick={() => (showCreateModal = true)}>지출 추가</Button>
        </div>
      </div>
    </header>

    <!-- Category Summary -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div class="grid grid-cols-3 md:grid-cols-6 gap-2">
        {#each categories as cat}
          <button
            onclick={() => (selectedCategory = selectedCategory === cat.key ? "" : cat.key)}
            class="p-3 rounded-xl text-center transition-all {selectedCategory === cat.key
              ? 'bg-primary-100 border-2 border-primary-500'
              : 'bg-surface border border hover:border-bright'}"
          >
            <span class="flex items-center justify-center w-6 h-6">{@html cat.icon}</span>
            <p class="text-xs text-secondary mt-1">{cat.label}</p>
            <p class="text-sm font-semibold text-primary">
              ₩{formatCurrency(categoryTotals[cat.key] || 0)}
            </p>
          </button>
        {/each}
      </div>
    </div>

    <!-- Filters -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
      <!-- 카테고리 필터 -->
      <div class="flex space-x-2 overflow-x-auto pb-2">
        <button
          onclick={() => (selectedCategory = "")}
          class="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors
            {selectedCategory === ''
            ? 'bg-primary-500 text-white'
            : 'bg-surface text-secondary hover:bg-surface-hover'}"
        >
          전체 ({expenses.length})
        </button>
        {#each categories as cat}
          {#if categoryTotals[cat.key]}
            <button
              onclick={() => (selectedCategory = cat.key)}
              class="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors
                {selectedCategory === cat.key
                ? 'bg-primary-500 text-white'
                : 'bg-surface text-secondary hover:bg-surface-hover'}"
            >
              <span class="inline-flex items-center justify-center w-4 h-4">{@html cat.icon}</span> {cat.label} ({expenses.filter(e => e.category === cat.key).length})
            </button>
          {/if}
        {/each}
      </div>
      
      <!-- 입력 방식 필터 -->
      <div class="flex items-center space-x-2 pt-2 border-t border mt-2">
        <span class="text-xs text-muted mr-2">입력 방식:</span>
        <button
          onclick={() => (selectedEntryMethod = "")}
          class="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors
            {selectedEntryMethod === ''
            ? 'bg-surface text-primary'
            : 'bg-surface-hover text-secondary hover:bg-surface-hover'}"
        >
          전체
        </button>
        <button
          onclick={() => (selectedEntryMethod = "manual")}
          class="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1
            {selectedEntryMethod === 'manual'
            ? 'bg-surface text-primary'
            : 'bg-surface-hover text-secondary hover:bg-surface-hover'}"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg> 수동 입력 ({entryMethodCounts.manual})
        </button>
        <button
          onclick={() => (selectedEntryMethod = "ocr")}
          class="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1
            {selectedEntryMethod === 'ocr'
            ? 'bg-indigo-600 text-white'
            : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg> 영수증 스캔 ({entryMethodCounts.ocr})
        </button>
        {#if entryMethodCounts.withReceipt > 0}
          <span class="text-xs text-muted px-2">|</span>
          <span class="text-xs text-muted">
            <svg class="w-3.5 h-3.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg> 영수증 첨부: {entryMethodCounts.withReceipt}건
          </span>
        {/if}
      </div>
    </div>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      {#if filteredExpenses.length === 0}
        <div class="bg-surface rounded-xl">
          <EmptyState
            icon="expense"
            title={selectedCategory || selectedEntryMethod ? "조건에 맞는 지출이 없습니다" : "지출 내역이 없습니다"}
            description={selectedCategory || selectedEntryMethod ? "다른 필터를 선택하거나 새 지출을 추가하세요" : "첫 번째 지출을 기록해보세요!"}
          >
            <Button variant="primary" onclick={() => (showCreateModal = true)}>지출 추가하기</Button>
          </EmptyState>
        </div>
      {:else}
        <ExpenseList 
          expenses={filteredExpenses} 
          onSelect={(expense) => openExpenseDetail(expense)}
          onOpenParticipants={(expense) => openParticipantSelector(expense)}
        />
      {/if}
    </main>

    <!-- Expense Detail Modal -->
    <ExpenseDetailModal
      show={showDetailModal}
      expense={selectedExpense}
      onClose={closeExpenseDetail}
    />

    <!-- Create Expense Modal -->
    <Modal
      show={showCreateModal}
      title="지출 추가"
      size="md"
      onClose={() => {
        showCreateModal = false;
        resetForm();
      }}
    >
      <form onsubmit={(e) => { e.preventDefault(); createExpense(); }} class="space-y-4">
        <!-- OCR Button -->
        <div class="flex justify-center pb-4 border-b border">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            class="hidden"
            bind:this={fileInput}
            onchange={handleFileUpload}
          />
          <button
            type="button"
            onclick={() => fileInput?.click()}
            class="flex items-center justify-center px-4 py-3 bg-indigo-50 text-indigo-700 rounded-xl hover:bg-indigo-100 transition-colors w-full font-medium"
            disabled={analyzing}
          >
            {#if analyzing}
              <svg class="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              영수증 분석 중...
            {:else}
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              영수증 스캔 (자동 입력)
            {/if}
          </button>
        </div>

        <div>
          <label for="merchantName" class="label">상호명</label>
          <input
            id="merchantName"
            type="text"
            bind:value={newExpense.merchantName}
            class="input"
            placeholder="스타벅스"
            required
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="amount" class="label">금액</label>
            <input
              id="amount"
              type="number"
              bind:value={newExpense.originalAmountCents}
              class="input"
              placeholder="15000"
              min="0"
              required
            />
          </div>
          <div>
            <label for="currency" class="label">통화</label>
            <select
              id="currency"
              bind:value={newExpense.originalCurrency}
              class="input"
            >
              <option value="KRW">KRW (원)</option>
              <option value="USD">USD ($)</option>
              <option value="JPY">JPY (¥)</option>
              <option value="EUR">EUR (€)</option>
            </select>
          </div>
        </div>

        <div>
          <span class="label">카테고리</span>
          <div class="grid grid-cols-3 gap-2">
            {#each categories as cat}
              <button
                type="button"
                onclick={() => (newExpense.category = cat.key)}
                class="p-3 rounded-lg text-center border-2 transition-colors
                  {newExpense.category === cat.key
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-bright hover:border-bright'}"
              >
                <span class="flex items-center justify-center w-6 h-6">{@html cat.icon}</span>
                <p class="text-sm mt-1">{cat.label}</p>
              </button>
            {/each}
          </div>
        </div>

        <div>
          <label for="date" class="label">날짜</label>
          <input
            id="date"
            type="date"
            bind:value={newExpense.transactedAt}
            class="input"
            required
          />
        </div>

        <div>
          <label for="description" class="label">메모 (선택)</label>
          <textarea
            id="description"
            bind:value={newExpense.description}
            class="input"
            rows="2"
            placeholder="추가 메모..."
          ></textarea>
        </div>

        <div class="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="secondary" onclick={() => { showCreateModal = false; resetForm(); }}>취소</Button>
          <Button type="submit" variant="primary" loading={loading || analyzing}>추가하기</Button>
        </div>
      </form>
    </Modal>

    <!-- Expense Participant Selector -->
    {#if selectedExpenseId}
      <ExpenseParticipantSelector
        expenseId={selectedExpenseId}
        {companions}
        currentParticipants={selectedExpenseParticipants}
        show={showParticipantSelector}
        onClose={closeParticipantSelector}
        onSave={handleParticipantsSaved}
      />
    {/if}
  </div>
</Layout>
